import { Pet, PetRole, PetStatus } from 'shared/@generated/models';

type PetStatusPet = Pick<Pet, 'status' | 'deleted_at' | 'deceased'>;
type PetPurchasePet = PetStatusPet &
  Pick<Pet, 'role'> & {
    pet_purchase?: unknown | null;
  };
type PetStatusContext = {
  listingDeleted?: boolean;
};

type PetStatusVariant = PetStatus | 'deleted' | 'deceased';

const statusOrder: Record<PetStatusVariant, number> = {
  [PetStatus.Available]: 0,
  [PetStatus.Reserved]: 1,
  [PetStatus.Sold]: 2,
  deceased: 3,
  deleted: 4,
};

export function getPetStatusVariant(
  pet: PetStatusPet,
  context?: PetStatusContext,
): PetStatusVariant {
  if (context?.listingDeleted || pet.deleted_at) {
    return 'deleted';
  }

  if (pet.deceased) {
    return 'deceased';
  }

  return pet.status ?? PetStatus.Available;
}

export function isPetAvailable(pet: PetStatusPet, context?: PetStatusContext): boolean {
  return getPetStatusVariant(pet, context) === PetStatus.Available;
}

export function canCreatePetPurchase(pet: PetPurchasePet): boolean {
  return (
    pet.role === PetRole.Offspring &&
    !pet.deleted_at &&
    !pet.deceased &&
    !pet.pet_purchase &&
    pet.status !== PetStatus.Sold
  );
}

export function canUpdatePetStatus(pet: PetPurchasePet): boolean {
  return pet.role === PetRole.Offspring && !pet.deleted_at && !pet.deceased && !pet.pet_purchase;
}

export function getPetStatusDotClassName(pet: PetStatusPet, context?: PetStatusContext): string {
  const variant = getPetStatusVariant(pet, context);

  if (variant === PetStatus.Available) {
    return 'bg-green-500';
  }

  if (variant === PetStatus.Reserved) {
    return 'bg-amber-500';
  }

  return 'bg-red-500';
}

export function getPetStatusTextClassName(pet: PetStatusPet, context?: PetStatusContext): string {
  const variant = getPetStatusVariant(pet, context);

  if (variant === PetStatus.Available) {
    return 'text-stone-950';
  }

  if (variant === PetStatus.Reserved) {
    return 'text-amber-950';
  }

  return 'text-red-950';
}

export function getPetStatusSortOrder(pet: PetStatusPet, context?: PetStatusContext): number {
  return statusOrder[getPetStatusVariant(pet, context)];
}
